import { Taxonomies } from '@src/global/taxonomies';

import { mapAlertParams } from './paramMappers/alerts';
import { mapArticleParams } from './paramMappers/articles';
import { mapAttachmentParams } from './paramMappers/attachments';
import { mapCategoryParams } from './paramMappers/categories';
import { mapCollateralParams } from './paramMappers/collaterals';
import { mapFieldManagementNewsParams } from './paramMappers/fieldManagementNews';
import { mapGuideParams } from './paramMappers/guides';
import { mapHomeBannerParams } from './paramMappers/home_banners';
import { mapImageParams } from './paramMappers/images';
import { mapImportParams } from './paramMappers/imports';
import { mapJobAidParams } from './paramMappers/jobAids';
import { mapProductParams } from './paramMappers/products';
import { mapPlatformParams } from './paramMappers/platforms';
import { mapProductUpdateParams } from './paramMappers/productUpdates';
import { mapImpactingPolicyParams } from './paramMappers/impactingPolicies';
import { mapReassignmentParams } from './paramMappers/reassignments';
import { mapSimplePageParam } from './paramMappers/simplePages';
import { mapSocialSharingParam } from './paramMappers/socialSharings';
import { mapTrainingParams } from './paramMappers/trainings';
import { mapHomeLinkParams } from './paramMappers/home_links';
import { mapWebinarParams } from './paramMappers/webinars';
import { mapAgencyParams } from './paramMappers/agencies';
import { mapUserParams } from './paramMappers/users';
import { mapRedirectParams } from './paramMappers/redirects';
import { mapRoleParams } from './paramMappers/roles';
import { mapBrandParams } from './paramMappers/brands';
import { mapCoBrandedVideoCategoryParams } from './paramMappers/co_branded_video_categories';
import { mapCoBrandedVideoLogoParams } from './paramMappers/co_branded_video_logos';
import { mapCoBrandedVideoTemplateParams } from './paramMappers/co_branded_video_templates';
import { mapCoBrandedVideoRequestParams } from './paramMappers/co_branded_video_requests';
import { mapCollateralCategoryParams } from './paramMappers/collateral_categories';
import { mapCollateralPriceParams } from './paramMappers/collateral_prices';
import { mapCollateralLogoParams } from './paramMappers/collateral_logos';
import { mapCollateralTemplateParams } from './paramMappers/collateral_templates';
import { mapCollateralDigitalAssetParams } from './paramMappers/collateral_digital_assets';
import { mapCollateralRequestParams } from './paramMappers/collateral_requests';
import { mapTagParams } from './paramMappers/tags';
import { mapSearchPhraseParams } from './paramMappers/searchPhrases';
import { mapMatchingSearchParams } from './paramMappers/matchingSearches';
import { mapPremierAccessGroupsImportsParams } from './paramMappers/premier_access_groups_imports';
import { mapAgencyLocationParams } from './paramMappers/agency_locations';
import { mapCustomerListParams } from './paramMappers/customer_lists';
import { mapUserProductsAuthorityImportsParams } from './paramMappers/user_products_authority_imports';
import { mapImportUsersHistoriesParams } from './paramMappers/import_users_histories';
import { mapBannerParams } from './paramMappers/banners';
import { mapProductPageParams } from './paramMappers/productPages';
import { mapHolidayGiftRequestParams } from './paramMappers/holiday_gift_requests';

/**
 * Generate promize that will return parameters that should be passed
 * to the body
 * @param {string} resource - Resource for which parameters should be processed
 * @param {Hash} data - data to convert to parameters
 * @param {string} method - HTTP method to be used for request
 * @returns {Promise} - Promise object that on resolve will return parameters
 */

// eslint-disable-next-line complexity
const generateBodyParams = (resource, data, method) => {
    const isCreate = method === 'POST';

    // Handle undefined data
    if (data === undefined || data === null) {
        return new Promise((resolve) => {
            resolve({});
        });
    }

    try {
        switch (resource) {
            case 'alerts':
                return mapAlertParams(isCreate, data);
            case 'articles':
                return mapArticleParams(isCreate, data);
            case 'attachments':
                return mapAttachmentParams(isCreate, data);
            case 'carrier_commissions/imports':
                return mapImportParams(isCreate, data);
            case 'collaterals':
                return mapCollateralParams(isCreate, data);
            case 'field_management_news':
                return mapFieldManagementNewsParams(isCreate, data);
            case 'guides':
                return mapGuideParams(isCreate, data);
            case 'home_banners':
                return mapHomeBannerParams(isCreate, data);
            case 'images':
                return mapImageParams(isCreate, data);
            case 'job_aids':
                return mapJobAidParams(isCreate, data);
            case 'all_products':
                return mapProductParams(isCreate, data);
            case 'platforms':
                return mapPlatformParams(isCreate, data);
            case 'product_updates':
                return mapProductUpdateParams(isCreate, data);
            case 'impacting_policies':
                return mapImpactingPolicyParams(isCreate, data);
            case 'reassignment':
                return mapReassignmentParams(isCreate, data);
            case 'simple_pages':
                return mapSimplePageParam(isCreate, data);
            case 'social_sharings':
                return mapSocialSharingParam(isCreate, data);
            case `taxonomies/${Taxonomies.salesAndMarketing}/categories`:
            case `taxonomies/${Taxonomies.productNewsAndUpdates}/categories`:
            case `taxonomies/${Taxonomies.systemAndTechnical}/categories`:
            case `taxonomies/${Taxonomies.collateral}/categories`:
                return mapCategoryParams(isCreate, data);
            case 'trainings':
                return mapTrainingParams(isCreate, data);
            case 'home_links':
                return mapHomeLinkParams(isCreate, data);
            case 'agencies':
                return mapAgencyParams(isCreate, data);
            case 'users':
                return mapUserParams(isCreate, data);
            case 'webinars':
                return mapWebinarParams(isCreate, data);
            case 'redirect_records':
                return mapRedirectParams(isCreate, data);
            case 'roles':
                return mapRoleParams(isCreate, data);
            case 'brands':
                return mapBrandParams(isCreate, data);
            case 'co_branded_video_categories':
                return mapCoBrandedVideoCategoryParams(isCreate, data);
            case 'co_branded_video_logos':
                return mapCoBrandedVideoLogoParams(isCreate, data);
            case 'co_branded_video_templates':
                return mapCoBrandedVideoTemplateParams(isCreate, data);
            case 'co_branded_video_requests':
                return mapCoBrandedVideoRequestParams(isCreate, data);
            case 'collateral_categories':
                return mapCollateralCategoryParams(isCreate, data);
            case 'collateral_logos':
                return mapCollateralLogoParams(isCreate, data);
            case 'collateral_prices':
                return mapCollateralPriceParams(isCreate, data);
            case 'collateral_templates':
                return mapCollateralTemplateParams(isCreate, data);
            case 'collateral_digital_assets':
                return mapCollateralDigitalAssetParams(isCreate, data);
            case 'collateral_requests':
                return mapCollateralRequestParams(isCreate, data);
            case 'tags':
                return mapTagParams(isCreate, data);
            case 'search_phrases':
                return mapSearchPhraseParams(isCreate, data);
            case 'matching_searches':
                return mapMatchingSearchParams(isCreate, data);
            case 'premier_access_groups_imports':
                return mapPremierAccessGroupsImportsParams(isCreate, data);
            case 'agency_locations':
                return mapAgencyLocationParams(isCreate, data);
            case 'customer_lists':
                return mapCustomerListParams(isCreate, data);
            case 'user_products_authority_imports':
                return mapUserProductsAuthorityImportsParams(isCreate, data);
            case 'import_users_histories':
                return mapImportUsersHistoriesParams(isCreate, data);
            case 'banners':
                return mapBannerParams(isCreate, data);
            case 'product_pages':
                return mapProductPageParams(isCreate, data);
            case 'holiday_gift_requests':
                return mapHolidayGiftRequestParams(isCreate, data);
            default:
                return new Promise((_resolve, reject) => {
                    reject(new Error(`Wrong resource: ${resource}`));
                });
        }
    } catch (error) {
        return new Promise((_resolve, reject) => {
            reject(new Error(`Error processing ${resource}: ${error.message}`));
        });
    }
};

export { generateBodyParams };
